import * as Sentry from '@sentry/react'
import {projectName, projectVersion} from '@src/configs/project'
import {useMerchantStore} from '@src/store/merchant'

const UNKNOWN_DEVICE = 'unknown'
const UNKNOWN_PLATFORM = 'Unknown-Web'

function detectPlatform(): string {
  if (typeof navigator === 'undefined') return UNKNOWN_PLATFORM

  const userAgent = navigator.userAgent.toLowerCase()
  const isMobile = userAgent.includes('mobile')
  const isAndroid = userAgent.includes('android')

  // Chrome detection (order matters - more specific first)
  if (userAgent.includes('chrome')) {
    if (isAndroid) return 'Chrome-Android'
    if (isMobile) return 'Chrome-Mobile'
    return 'Chrome-Web'
  }

  // Safari detection
  if (userAgent.includes('safari')) {
    return isMobile ? 'Safari-Mobile' : 'Safari-Web'
  }

  // Other browsers
  if (userAgent.includes('firefox')) return 'Firefox-Web'
  if (userAgent.includes('edge')) return 'Edge-Web'

  return UNKNOWN_PLATFORM
}

function getSentryRelease(): string {
  try {
    const client = Sentry.getCurrentHub().getClient()
    const release = client?.getOptions()?.release

    if (release) {
      return release.includes('@') ? release.split('@')[1] ?? release : release
    }
  } catch (error) {
    console.warn('Error getting Sentry release, using fallback:', error)
  }

  return projectVersion
}

function generateDeviceId(): string {
  return Date.now().toString(36)
}

function generateCustomUserAgent(): string {
  // Safe access to import.meta.env for Cypress Node.js context
  const environment =
    typeof import.meta?.env?.VITE_ENVIRONMENT === 'string' ? import.meta.env.VITE_ENVIRONMENT : 'development'
  const isProduction = environment === 'production' || environment === 'prod'
  const deviceId = isProduction ? useMerchantStore.getState().deviceId ?? UNKNOWN_DEVICE : generateDeviceId()

  const release = getSentryRelease()
  const platform = detectPlatform()

  return `${deviceId}:${projectName}:${release}:${platform}`
}

let cachedUserAgent: string | null = null

export function getCustomUserAgent(): string {
  if (cachedUserAgent === null) {
    cachedUserAgent = generateCustomUserAgent()
  }
  return cachedUserAgent
}

export function resetCachedUserAgent(): void {
  cachedUserAgent = null
}
